/**
 * PushNotificationToggle – small UI control for enabling/disabling push notifications.
 *
 * Drop this into a settings page or notification preferences panel.
 *
 * Usage:
 *   import PushNotificationToggle from '@/components/notifications/PushNotificationToggle';
 *   <PushNotificationToggle />
 */

import React from 'react';
import usePushNotifications from '../../hooks/usePushNotifications';

export default function PushNotificationToggle() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
  } = usePushNotifications();

  if (!isSupported) {
    return (
      <div className="text-sm text-gray-400">
        Push notifications are not supported in this browser.
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="text-sm text-red-400">
        Notifications are blocked. Please enable them in your browser settings.
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-3">
        <button
          type="button"
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={loading}
          className={`
            relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border
            transition-colors duration-200 ease-in-out focus:outline-none
            focus-visible:ring-2 focus-visible:ring-primary/60
            focus-visible:ring-offset-2 focus-visible:ring-offset-backgroundSurface
            ${isSubscribed ? 'border-primary bg-primary' : 'border-borderDefault bg-backgroundBody hover:border-primary/50'}
            ${loading ? 'cursor-wait opacity-50' : 'cursor-pointer'}
          `}
          role="switch"
          aria-checked={isSubscribed}
          aria-label="Push notifications"
        >
          <span
            aria-hidden="true"
            className={`
              inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform
              ${isSubscribed ? 'translate-x-6' : 'translate-x-1'}
            `}
          />
        </button>
        <span className="min-w-0 text-sm text-textPrimary">
          {loading
            ? 'Updating…'
            : isSubscribed
              ? 'Push notifications enabled'
              : 'Enable push notifications'}
        </span>
      </div>
      {error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
